import { STRUCTURING_API, formatDate } from './structuringUtils';
import { toast } from 'react-toastify';
import { deleteInstallment, deleteDocument } from '../../../services/StructuredOperationsService';
import { exportToExcel } from '../../../services/ExcelExportService';

export type Receivable = {
    receivable_number: string;
    due_date: string;
    amount: number;
    v_pag?: string;
    nat_op?: string;
    ind_pag?: string;
};

export type Document = {
    assignor_name: string;
    assignor_cnpj: string;
    document_id: string;
    document_title: string;
    cfop: string;
    issue_date: string;
    xml_size_bytes: number;
    receivables: Receivable[];
    debtor_cpf_cnpj: string;
    debtor_name: string;
    debtor_email: string;
    address_street: string;
    address_number: string;
    address_complement: string;
    address_district: string;
    address_ibge_code: string;
    address_city: string;
    address_state: string;
    address_zip_code: string;
    address_country: string;
    address_phone: string;
    processing_status?: string;
    processing_error?: string | null;
};

export type ProcessingError = {
    file_name: string;
    error: string;
};

export type Operation = {
    operation_code: string;
    credit_operations_id?: string;
    total_documents: number;
    total_receivables: number;
    total_amount: number;
    processing_date: string;
    s3_location: string;
    documents: Document[];
    notes: string;
    status?: string;
    operation_type?: string;
    obligation_type?: string;
    trader?: string;
    validation_xml?: boolean;
    validation_receipt_stub?: boolean;
    validation_contact?: boolean;
    validation_sefaz?: boolean;
    validation_invoice_pdf?: boolean;
    validation_accepted_bill?: boolean;
    validation_purchase_contract?: boolean;
    sent_to_validation?: boolean;
    analysis_criteria?: string;
    analysis_receivables_count?: number;
    processing_errors?: ProcessingError[] | null;
};

export const buildFetchUrl = (id: string | undefined): string => {
    return `${STRUCTURING_API.url}/operations/${id}`;
};

export const buildFetchHeaders = () => [
    { key: 'X-API-Key', value: STRUCTURING_API.key }
];

export const parseOperationData = (data: any): Operation => {
    const operationCode = data.operation?.operation_code || data.operation_code || '';
    const totalAmount = data.operation?.total_amount || data.total_amount || 0;
    const totalReceivables = (data.documents || []).reduce(
        (acc: number, doc: Document) => acc + (doc.receivables?.length || 0),
        0
    );
    const cv = data.operation?.configure_validation || data.configure_validation;

    return {
        operation_code: operationCode,
        credit_operations_id: data.operation?.credit_operations_id || data.credit_operations_id || '',
        total_documents: data.operation?.total_documents || data.total_documents || 0,
        total_receivables: totalReceivables,
        total_amount: totalAmount,
        processing_date: data.operation?.processing_date || data.processing_date || '',
        s3_location: data.operation?.s3_location || data.s3_location || '',
        documents: data.documents || [],
        notes: data.notes || '',
        status: data.operation?.status || data.status || '',
        operation_type: cv?.operation_type || '',
        obligation_type: cv?.obligation_type || '',
        trader: cv?.trader || '',
        validation_xml: cv?.validation_xml ?? false,
        validation_receipt_stub: cv?.validation_receipt_stub ?? false,
        validation_contact: cv?.validation_contact ?? false,
        validation_sefaz: cv?.validation_sefaz ?? false,
        validation_invoice_pdf: cv?.validation_invoice_pdf ?? false,
        validation_accepted_bill: cv?.validation_accepted_bill ?? false,
        validation_purchase_contract: cv?.validation_purchase_contract ?? false,
        sent_to_validation: data.operation?.sent_to_validation ?? data.sent_to_validation ?? cv?.sent_to_validation ?? false,
        analysis_criteria: cv?.analysis_criteria || '',
        analysis_receivables_count: cv?.analysis_receivables_count ?? 0,
        processing_errors: data.operation?.processing_errors || data.processing_errors || null,
    };
};

export const reloadOperationData = async (id: string | undefined): Promise<Operation | null> => {
    try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(buildFetchUrl(id), {
            headers: {
                'X-API-Key': STRUCTURING_API.key,
                'Authorization': `Bearer ${token}`,
            }
        });

        if (response.ok) {
            const newData = await response.json();
            return parseOperationData(newData);
        }
        return null;
    } catch (error) {
        console.error('Error reloading data:', error);
        return null;
    }
};

export const handleDeleteDocument = async (
    operationCode: string,
    documentId: string
): Promise<{ success: boolean; operationDeleted: boolean; message: string }> => {
    try {
        const request = {
            action: 'delete_document' as const,
            operation_code: operationCode,
            document_id: documentId
        };

        const response = await deleteDocument(request);
        toast.success(response.message);

        return {
            success: true,
            operationDeleted: response.operation_deleted || false,
            message: response.message
        };
    } catch (error: any) {
        console.error('Delete error:', error);
        toast.error(error.message || 'Erro ao deletar');
        return {
            success: false,
            operationDeleted: false,
            message: error.message || 'Erro ao deletar'
        };
    }
};

export const handleDeleteInstallment = async (
    operationCode: string,
    documentId: string,
    installmentNumber: string
): Promise<{ success: boolean; message: string }> => {
    try {
        const request = {
            action: 'delete_installment' as const,
            operation_code: operationCode,
            document_id: documentId,
            installment_number: installmentNumber
        };

        const response = await deleteInstallment(request);
        toast.success(response.message);

        return {
            success: true,
            message: response.message
        };
    } catch (error: any) {
        console.error('Delete error:', error);
        toast.error(error.message || 'Erro ao deletar');
        return {
            success: false,
            message: error.message || 'Erro ao deletar'
        };
    }
};

export const buildTableHeaders = () => [
    { id: 'document_id', title: 'NFe', field: 'document_id', canSort: false, width: '120px' },
    { id: 'document_title', title: 'Título', field: 'document_title', canSort: false, width: '200px' },
    { id: 'receivable_number', title: 'Parcela', field: 'receivable_number', canSort: false, width: '100px', class: 'text-center' },
    { id: 'due_date', title: 'Vencimento', field: 'due_date', canSort: false, width: '150px', class: 'text-center' },
    { id: 'amount', title: 'Valor', field: 'amount', canSort: false, width: '240px', class: 'text-center' },
    { id: 'cedente_name', title: 'Cedente', field: 'assignor_name', canSort: false, width: '200px' },
    { id: 'debtor_name', title: 'Sacado', field: 'debtor_name', canSort: false, width: '200px' },
    { id: 'processing_status', title: 'Status', field: 'processing_status', canSort: false, width: '120px', class: 'text-center' },
    { id: 'processing_error', title: 'Erro', field: 'processing_error', canSort: false, width: '200px' },
    { id: 'actions', title: 'Ações', field: 'actions', canSort: false, width: '100px', class: 'text-center' }
];

export const expandDocumentsToRows = (documents: Document[]) => {
    const expandedData: any[] = [];

    documents.forEach(document => {
        document.receivables.forEach((receivable) => {
            expandedData.push({
                document_title: document.document_title,
                document_id: document.document_id || '',
                receivable_number: receivable.receivable_number,
                due_date: receivable.due_date ? receivable.due_date.split('T')[0].split('-').reverse().join('/') : '',
                amount: receivable.amount,
                assignor_name: document.assignor_name,
                debtor_name: document.debtor_name,
                processing_status: document.processing_status || '',
                processing_error: document.processing_error || '',
                receivable_raw: receivable,
                document_raw: document
            });
        });
    });

    return expandedData;
};

export const paginateData = (data: any[], currentPage: number, itemsPerPage: number) => {
    const totalItems = data.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = data.slice(startIndex, endIndex);

    return {
        paginatedData,
        totalItems,
        totalPages,
        startIndex,
        endIndex
    };
};

export const saveDocuments = async (payload: { credit_operations_id: string; documents: any[] }): Promise<{ success: boolean; message: string }> => {
    try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${STRUCTURING_API.url}/documents/save`, {
            method: 'POST',
            headers: {
                'X-API-Key': STRUCTURING_API.key,
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (response.ok) {
            return { success: true, message: data.message || 'Documentos salvos com sucesso' };
        }
        return { success: false, message: data.message || 'Erro ao salvar documentos' };
    } catch (error) {
        console.error('Erro ao salvar documentos:', error);
        return { success: false, message: 'Erro de conexão ao salvar documentos' };
    }
};

export const sendToValidation = async (op: Operation): Promise<{ success: boolean; sent_to_validation?: boolean }> => {
    try {
        const response = await fetch(`${STRUCTURING_API.url}/configure/validation`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-API-Key': STRUCTURING_API.key },
            body: JSON.stringify({
                credit_operations_id: op.credit_operations_id,
                operation_type: op.operation_type,
                obligation_type: op.obligation_type,
                trader: op.trader?.trim(),
                validation_xml: op.validation_xml || false,
                validation_receipt_stub: op.validation_receipt_stub || false,
                validation_contact: op.validation_contact || false,
                validation_sefaz: op.validation_sefaz || false,
                validation_invoice_pdf: op.validation_invoice_pdf || false,
                validation_accepted_bill: op.validation_accepted_bill || false,
                validation_purchase_contract: op.validation_purchase_contract || false,
                analysis_criteria: op.analysis_criteria,
                analysis_receivables_count: op.analysis_criteria === 'PARCIAL' ? op.analysis_receivables_count : op.total_receivables,
            }),
        });
        if (response.ok) {
            const result = await response.json().catch(() => ({}));
            toast.success('Enviado para validação com sucesso!');
            return { success: true, sent_to_validation: result.sent_to_validation ?? true };
        }
        const result = await response.json().catch(() => ({}));
        toast.error(result.message || 'Erro ao enviar para validação.');
        return { success: false };
    } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : 'Erro ao conectar com o servidor');
        return { success: false };
    }
};

export const removeValidation = async (creditOperationsId: string, justification: string): Promise<boolean> => {
    try {
        const response = await fetch(`${STRUCTURING_API.url}/configure/validation/remove`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-API-Key': STRUCTURING_API.key },
            body: JSON.stringify({ credit_operations_id: creditOperationsId, justification }),
        });
        if (response.ok) {
            toast.success('Removido da validação com sucesso!');
            return true;
        }
        const result = await response.json().catch(() => ({}));
        toast.error(result.message || 'Erro ao remover da validação.');
        return false;
    } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : 'Erro ao conectar com o servidor');
        return false;
    }
};

export const exportOperationToExcel = (operation: Operation) => {
    const exportData: any[] = [];
    operation.documents.forEach(doc => {
        doc.receivables.forEach(receivable => {
            exportData.push({
                indPag: receivable.ind_pag || '', natOp: receivable.nat_op || '', vPag: receivable.v_pag || '',
                operacao: operation.operation_code, cedente_nome: doc.assignor_name, cedente_cnpj: doc.assignor_cnpj,
                nfe: doc.document_id, titulo: doc.document_title, cpf_cnpj: doc.debtor_cpf_cnpj, nome: doc.debtor_name,
                logradouro: doc.address_street, numero: doc.address_number, complemento: doc.address_complement,
                bairro: doc.address_district, cod_ibge: doc.address_ibge_code, municipio: doc.address_city,
                uf: doc.address_state, cep: doc.address_zip_code, pais: doc.address_country,
                telefone: doc.address_phone, email: doc.debtor_email, cfop: doc.cfop,
                data_emissao: formatDate(doc.issue_date), ndup: receivable.receivable_number, vdup: receivable.amount,
                dvenc: receivable.due_date ? receivable.due_date.split('T')[0].split('-').reverse().join('/') : '',
                status: doc.processing_status || '',
                erro: doc.processing_error || '',
                valor_corrigido: '', vencimento_corrigido: '', parcela_adquirida: '',
            });
        });
    });
    const headers = [
        { id: 'indPag', title: 'indPag', field: 'indPag', canSort: false },
        { id: 'natOp', title: 'natOp', field: 'natOp', canSort: false },
        { id: 'vPag', title: 'vPag', field: 'vPag', canSort: false },
        { id: 'operacao', title: 'Operação', field: 'operacao', canSort: false },
        { id: 'cedente_nome', title: 'Cedente Nome', field: 'cedente_nome', canSort: false },
        { id: 'cedente_cnpj', title: 'Cedente CNPJ', field: 'cedente_cnpj', canSort: false },
        { id: 'nfe', title: 'NFe', field: 'nfe', canSort: false },
        { id: 'titulo', title: 'Titulo', field: 'titulo', canSort: false },
        { id: 'cpf_cnpj', title: 'Sacado CNPJ/CPF', field: 'cpf_cnpj', canSort: false },
        { id: 'nome', title: 'Sacado Nome', field: 'nome', canSort: false },
        { id: 'logradouro', title: 'Logradouro', field: 'logradouro', canSort: false },
        { id: 'numero', title: 'Numero', field: 'numero', canSort: false },
        { id: 'complemento', title: 'Complemento', field: 'complemento', canSort: false },
        { id: 'bairro', title: 'bairro', field: 'bairro', canSort: false },
        { id: 'cod_ibge', title: 'CodIBGE', field: 'cod_ibge', canSort: false },
        { id: 'municipio', title: 'municipio', field: 'municipio', canSort: false },
        { id: 'uf', title: 'UF', field: 'uf', canSort: false },
        { id: 'cep', title: 'CEP', field: 'cep', canSort: false },
        { id: 'pais', title: 'Pais', field: 'pais', canSort: false },
        { id: 'telefone', title: 'Telefone', field: 'telefone', canSort: false },
        { id: 'email', title: 'Email', field: 'email', canSort: false },
        { id: 'cfop', title: 'CFOP', field: 'cfop', canSort: false },
        { id: 'data_emissao', title: 'Data Emissão', field: 'data_emissao', canSort: false },
        { id: 'ndup', title: 'nDup', field: 'ndup', canSort: false },
        { id: 'vdup', title: 'vDup', field: 'vdup', canSort: false },
        { id: 'dvenc', title: 'dVenc', field: 'dvenc', canSort: false },
        { id: 'status', title: 'Status', field: 'status', canSort: false },
        { id: 'erro', title: 'Erro', field: 'erro', canSort: false },
        { id: 'valor_corrigido', title: 'Valor Corrigido', field: 'valor_corrigido', canSort: false },
        { id: 'vencimento_corrigido', title: 'vencimento corrigido', field: 'vencimento_corrigido', canSort: false },
        { id: 'parcela_adquirida', title: 'Parcela adquirida', field: 'parcela_adquirida', canSort: false },
    ];
    exportToExcel(headers, exportData, `operacao-${operation.operation_code}.csv`);
};
